import {useEffect, useMemo, useState} from "react";
import {Alert, Form} from "react-bootstrap";
import {Link} from "react-router";
import {PageHeader} from "../../components/PageHeader";
import {TrademarkNotices, trademarksIn} from "../../components/TrademarkNotices";
import {GLOSSARY_PATH, glossaryData} from "../../structuredData";
import {docs, format, glossary, GlossaryEntry} from "../../i18n";

const messages = docs.glossary;

// What the filter compares: lower case, without accents.
const folded = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const firstLetter = (entry: GlossaryEntry) => folded(entry.term).charAt(0).toUpperCase();

// Synthesizer terms for beginners, shared by every product's documentation, which links to an entry as
// /docs/glossary#term-{id}. Alphabetical, by letter, with a filter. Static: it needs nothing from the API.
export function GlossaryPage() {
    const [query, setQuery] = useState('');
    const entries = useMemo(() => [...glossary].sort((a, b) => a.term.localeCompare(b.term, undefined, { sensitivity: 'base' })), []);
    const byId = useMemo(() => new Map(entries.map(x => [x.id, x])), [entries]);

    const wanted = folded(query.trim());
    const shown = wanted
        ? entries.filter(x => [x.term, ...(x.aliases ?? []), x.definition].some(text => folded(text).includes(wanted)))
        : entries;
    // Matches on the name first: someone typing "lfo" wants LFO before the entries that mention it.
    const named = (x: GlossaryEntry) => [x.term, ...(x.aliases ?? [])].some(text => folded(text).includes(wanted));
    const ordered = wanted ? [...shown.filter(named), ...shown.filter(x => !named(x))] : shown;
    const letters = [...new Set(entries.map(firstLetter))];
    const groups = wanted ? [{ letter: undefined, entries: ordered }]
        : letters.map(letter => ({ letter, entries: ordered.filter(x => firstLetter(x) === letter) }));

    // A link to an entry (/docs/glossary#term-lfo) from another page: scrolled to once it's rendered.
    useEffect(() => {
        const id = decodeURIComponent(window.location.hash.substring(1));
        if (id) {
            window.document.getElementById(id)?.scrollIntoView();
        }
    }, []);

    // Following a "See also" link while filtering: clear the filter so the entry is there to go to.
    const follow = (id: string) => {
        if (wanted) {
            setQuery('');
            requestAnimationFrame(() => window.document.getElementById(`term-${id}`)?.scrollIntoView());
        }
    };

    return (
        <>
            <div className="mb-2"><Link to="/docs" className="small"><i className="bi bi-arrow-left me-1"></i>{docs.page.all}</Link></div>
            <PageHeader title={messages.title} description={messages.description} path={GLOSSARY_PATH}
                        jsonLd={glossaryData(messages.title, messages.description, entries)}/>
            <p className="lead fs-6 col-lg-9">{messages.intro}</p>
            <div className="glossary col-lg-9">
                <Form.Control type="search" className="mb-3" placeholder={messages.filter} aria-label={messages.filter}
                              value={query} onChange={e => setQuery(e.target.value)}/>
                {!wanted && (
                    <nav aria-label={messages.letters} className="mb-4">
                        <ul className="list-inline mb-0">
                            {letters.map(letter => (
                                <li key={letter} className="list-inline-item"><a href={`#letter-${letter}`} className="px-1">{letter}</a></li>
                            ))}
                        </ul>
                    </nav>
                )}
                {ordered.length === 0 && <Alert variant="secondary">{format(messages.noMatches, { query: query.trim() })}</Alert>}
                {groups.filter(x => x.entries.length).map(group => (
                    <section key={group.letter ?? 'matches'} aria-labelledby={group.letter ? `letter-${group.letter}` : undefined}>
                        {group.letter && <h2 id={`letter-${group.letter}`} className="h4 border-bottom pb-1 mt-4">{group.letter}</h2>}
                        <dl className="mb-0">
                            {group.entries.map(entry => (
                                <div key={entry.id}>
                                    <dt id={`term-${entry.id}`} className="h6 mb-1">{entry.term}</dt>
                                    <dd>
                                        <p className="mb-1">{entry.definition}</p>
                                        {!!entry.aliases?.length && (
                                            <p className="small text-body-secondary mb-1">{format(messages.alsoCalled, { names: entry.aliases.join(', ') })}</p>
                                        )}
                                        {!!entry.seeAlso?.length && (
                                            <p className="small mb-0">
                                                {messages.seeAlso}{' '}
                                                {entry.seeAlso.filter(id => byId.has(id)).map((id, i) => (
                                                    <span key={id}>
                                                        {i > 0 && ', '}
                                                        <a href={`#term-${id}`} onClick={() => follow(id)}>{byId.get(id)!.term}</a>
                                                    </span>
                                                ))}
                                            </p>
                                        )}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </section>
                ))}
                <TrademarkNotices marks={trademarksIn(...entries.map(x => x.definition))} className="mt-5"/>
            </div>
        </>
    );
}
